/**
 * useTopSpenders — fetches GET /api/v1/reports/top-spenders.
 * useFanDetail   — fetches GET /api/v1/reports/fan-detail/:fanHandle.
 *
 * Per CEO directive 2026-04-28: no mock anywhere. On any failure the hook
 * surfaces the error and `data` stays null — the UI is responsible for
 * rendering an explicit error state so we never silently show fabricated
 * numbers. (Earlier this hook returned a labelled MOCK_TOP_SPENDERS payload
 * which made an empty / broken backend look like a healthy report.)
 */
import { useEffect, useState, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
const FETCH_TIMEOUT_MS = 8000;  // top-spenders aggregate can be slower than investor

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`timeout after ${ms}ms`));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useTopSpenders({ from, to, limit, minSpend, rangeId }, refreshKey = 0) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const controller = new AbortController();
    const params = new URLSearchParams();
    if (from)     params.set("from", from);
    if (to)       params.set("to", to);
    if (limit)    params.set("limit", String(limit));
    if (minSpend != null) params.set("minSpend", String(minSpend));
    if (rangeId)  params.set("rangeId", rangeId);
    try {
      const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = {};
      if (token)       headers.Authorization = `Bearer ${token}`;
      else if (apiKey) headers["X-API-Key"]  = apiKey;
      const res = await withTimeout(
        fetch(`${API_BASE}/reports/top-spenders?${params}`, {
          headers,
          signal: controller.signal,
        }),
        FETCH_TIMEOUT_MS,
        controller,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
      setData(json.data);
    } catch (e) {
      setError(e.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, limit, minSpend]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey, from, to, limit, minSpend]);

  return { data, error, loading, reload: load };
}

// ────────────────────────────────────────────────────────────
//  Per-fan drill-in.
// ────────────────────────────────────────────────────────────
export function useFanDetail(fanHandle, opts = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fanHandle) return;
    setLoading(true); setError(null);
    const controller = new AbortController();
    const params = new URLSearchParams();
    if (opts.from) params.set("from", opts.from);
    if (opts.to)   params.set("to", opts.to);
    (async () => {
      try {
        const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
        const apiKey = process.env.REACT_APP_API_KEY;
        const headers = {};
        if (token)       headers.Authorization = `Bearer ${token}`;
        else if (apiKey) headers["X-API-Key"]  = apiKey;
        const res = await withTimeout(
          fetch(`${API_BASE}/reports/fan-detail/${encodeURIComponent(fanHandle)}?${params}`, {
            headers,
            signal: controller.signal,
          }),
          FETCH_TIMEOUT_MS,
          controller,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
        setData(json.data);
      } catch (e) {
        setError(e.message || String(e));
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fanHandle, opts.from, opts.to]);

  return { data, error, loading };
}

// ────────────────────────────────────────────────────────────
//  CSV builder — same column order as the table.
// ────────────────────────────────────────────────────────────
export function buildTopSpendersCSV(data) {
  const rows = data?.rows || [];
  const f = data?.filters || {};
  const headerLines = [
    `# Top Spending Fans — Fancall Relational Report`,
    `# Generated: ${new Date().toISOString()}`,
    `# Window: ${f.from || ""} → ${f.to || ""}`,
    `# Filter: top ${f.limit || ""} fans, min spend Rs ${f.minSpend ?? 0}`,
    `# Rows: ${rows.length}`,
    `#`,
  ];
  const cols = [
    "fanHandle", "totalSpent", "creatorsPaid", "callCount", "avgPerCall",
    "topCreatorName", "topCreatorSpent", "topCreatorPct",
    "firstCallAt", "lastCallAt", "lifetimeRefunds",
  ];
  const headerRow = cols.join(",");
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const dataRows = rows.map(r => cols.map(c => escape(r[c])).join(","));
  return [...headerLines, headerRow, ...dataRows].join("\n");
}
